import { useEffect, useMemo, useRef, useState } from 'react';
import { NestedSubAccount } from 'shared/subAccountList';
import { OrgChart, OrgChartTreeNode, changePage, getInitialExpansion, revealNode, toggleNode } from 'shared/orgChart';
import OrgItem from './OrgItem/OrgItem';
import styles from './OrgChartContainer.module.scss';
import { OrgChart as D3OrgChart } from 'd3-org-chart';
import {
  AccountTransferToResponse,
  Box,
  CircleButton,
  MaterialSymbol,
  PulloverSelect,
  SubAccountBalanceView,
  UtilButton,
  withStyles,
  LoadingIndicator,
} from 'vopay';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';
import CreateSubAccount from './CreateSubAccount/CreateSubAccount';
import AccountTransfer from './AccountTransfer/AccountTransfer';
import AccountNode from './AccountNode';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY } from '@/hooks/useApi/useUserSettings';
const cn = withStyles(styles);

type OrgChartContainerProps = {
  subAccounts: NestedSubAccount[];
  canCreateAccountTransfer: boolean;
  accountBlocked: boolean;
  canManageBlocking: boolean;
  refresh: () => void;
  onBlockAccount: (accountID: string, accountName: string) => void;
  onUnblockAccount: (accountID: string, accountName: string) => void;
  rootNextPage?: () => void;
  rootPrevPage?: () => void;
  hasRootNextServerPage?: boolean;
  hasRootPrevServerPage?: boolean;
  rootPageSize?: number;
};

// An interface, not a type alias: a type alias can't name itself as the engine node's child type.
export interface ExtendedNestedSubAccount
  extends Omit<NestedSubAccount, 'SubAccounts'>,
    OrgChartTreeNode<
      ExtendedNestedSubAccount,
      'subAccount' | 'accountNode' | 'loadNextServerPage' | 'loadPreviousServerPage'
    > {
  isBlurred: boolean;
  isOpen: boolean;
  isRoot: boolean;
  breadcrumb: string[];
  parentsIds: string[];
  selectedBalanceType: SubAccountBalanceView;
}

export default function OrgChartContainer({
  subAccounts,
  canCreateAccountTransfer,
  accountBlocked,
  canManageBlocking,
  refresh,
  onBlockAccount,
  onUnblockAccount,
  rootNextPage,
  rootPrevPage,
  hasRootNextServerPage,
  hasRootPrevServerPage,
  rootPageSize = 10,
}: OrgChartContainerProps) {
  const {
    response: { AccountName, AccountID },
  } = useClientData();

  const { balances, fetchingBalances } = useAccountBalances();
  const { userSettings } = useUserSettingsProvider();
  const [displayLevel, setDisplayLevel] = useState<number>(2);
  const [showUpto, setShowUpto] = useState<number>(10);
  const [isExpand, setIsExpand] = useState<boolean>(false);

  const [displayBalance, setDisplayBalance] = useState<SubAccountBalanceView>(
    userSettings.SubAccountBalanceDisplay || DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY,
  );

  const [extendedSubAccount, setExtendedSubAccount] = useState<ExtendedNestedSubAccount>();
  const [menuOpen, setMenuOpen] = useState<boolean>(true);
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const chartRef = useRef<D3OrgChart<ExtendedNestedSubAccount> | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart' });

  // Root level: no client-side paging — every next/prev at this level is a server request, so the chart keeps the
  // root's children whole and draws the server-paging nodes at their ends.
  const getRootServerPageNodes = (node: ExtendedNestedSubAccount) => {
    if (!node.isRoot) {
      return undefined;
    }

    const baseNode = node.children?.[0] ?? {
      ...node,
      level: node.level + 1,
      parentId: node.id,
    };

    const serverPageNode = (
      nodeType: 'loadNextServerPage' | 'loadPreviousServerPage',
      id: string,
    ): ExtendedNestedSubAccount => ({
      ...baseNode,
      isExpanded: false,
      childrenPage: 0,
      isRoot: false,
      linked: false,
      id,
      nodeType,
      numChildren: 0,
      children: [],
    });

    return {
      before: hasRootPrevServerPage
        ? [serverPageNode('loadPreviousServerPage', `${node.id}-load-prev-server-page`)]
        : [],
      after: hasRootNextServerPage ? [serverPageNode('loadNextServerPage', `${node.id}-load-next-server-page`)] : [],
    };
  };

  const transformSubAccount = (
    { SubAccounts, ...subAccount }: NestedSubAccount,
    level: number,
    parentId: string,
    breadcrumb: string[],
    parentsIds: string[],
  ): ExtendedNestedSubAccount => {
    // preserve expansion info on refresh

    return {
      ...subAccount,
      ...getInitialExpansion(level, !!SubAccounts?.length, displayLevel),

      breadcrumb: [...breadcrumb],
      parentsIds: [...parentsIds],
      linked: true,
      isRoot: level === 0,
      level,
      id: subAccount.Key,
      numChildren: SubAccounts?.length || 0,
      parentId,
      nodeType: level == 0 ? 'accountNode' : 'subAccount',
      isBlurred: false,
      isOpen: false,
      selectedBalanceType: displayBalance === 'do-not-show-balances' ? 'available-balance' : displayBalance,
      children: SubAccounts?.map((child) =>
        transformSubAccount(
          child,
          level + 1,
          subAccount.Key,
          [...breadcrumb, subAccount.AccountName],
          [...parentsIds, subAccount.Key],
        ),
      ),
    };
  };

  const resetList = () => {
    const root: NestedSubAccount = {
      LegalBusinessName: '',
      ParentAccountName: '',
      ParentAccountID: '',
      AccountID: '',
      OriginatorName: '',
      OriginatorShortName: '',
      Email: '',
      Phone: '',
      Fax: '',
      City: '',
      Province: '',
      Country: '',
      PostalCode: '',
      Address: '',
      Address2: '',
      FlinksUrl: '',
      IsActive: false,
      AccountStatus: '',
      GCMEnabled: false,
      EFTCollectEnabled: false,
      EFTSendEnabled: false,
      USDEFTCollectEnabled: false,
      USDEFTSendEnabled: false,
      ACHFundEnabled: false,
      ACHWithdrawEnabled: false,
      RTRWithdrawEnabled: false,
      InteracMoneyRequestEnabled: false,
      InteracBulkPayoutEnabled: false,
      InboundEtransferEnabled: false,
      IQ11StandardEnabled: false,
      IQ11BoostEnabled: false,
      IQ11PlusEnabled: false,
      IQ11AdvantageEnabled: false,
      PortalViewOnly: false,
      AddUserEnabled: false,
      AutoBalanceTransferEnabled: false,
      MaxFundEFTAmount: '',
      MaxWithdrawEFTAmount: '',
      MaxGCMAmount: '',
      MaxInteracMoneyAmount: '',
      MaxInteracBulkPayoutAmount: '',
      MaxRTRWithdrawAmount: '',
      ClientAccountsEnabled: false,
      SubAccountType: '',
      TransactionServiceDefault: '',
      PayLinkEnabled: false,
      IsBusinessCaseSet: false,

      Token: '',
      WebhookUrl: '',
      SigningAuthorities: {},
      AccountingSoftwareIntegrationEnabled: false,
      Currency: '',
      AccountBalance: '',
      PendingBalance: '',
      Reserve: '',
      AvailableImmediately: '',
      AvailableBalance: '',
      DailyLimitAmount: '',
      WeeklyLimitAmount: '',
      MonthlyLimitAmount: '',
      MaxVisaDirectAmount: '',
      MonthlyTransactionLimit: '',
      AFTEnabled: false,
      KYCLightEnabled: false,
      AccountName,
      SubAccountsEnabled: true,
      NumClientAccounts: 0,
      Key: AccountID,
      SubAccounts: subAccounts,
      childSubAccounts: [],
      ParentAccounts: [],
    };

    setExtendedSubAccount(transformSubAccount(root, 0, '', [], []));
    setIsInitialState(true);
  };

  useEffect(() => {
    setMenuOpen(false);
    resetList();
  }, [subAccounts]);

  // Update all nodes' selectedBalanceType when displayBalance changes
  useEffect(() => {
    if (!extendedSubAccount) return;

    const updateAllNodesBalanceType = (node: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
      const newBalanceType = displayBalance === 'do-not-show-balances' ? 'available-balance' : displayBalance;

      return {
        ...node,
        selectedBalanceType: newBalanceType,
        children: node.children?.map(updateAllNodesBalanceType),
      };
    };

    setExtendedSubAccount(updateAllNodesBalanceType(extendedSubAccount));
  }, [displayBalance]);

  const handleExpand = (id: string, level: number, parentIds: string[]) => {
    setIsInitialState(false);

    if (extendedSubAccount) {
      const updatedSubAccount = toggleNode(extendedSubAccount, { id, level, ancestorIDs: parentIds });
      setExtendedSubAccount(updatedSubAccount);

      return updatedSubAccount;
    }

    return extendedSubAccount;
  };

  const changeMenuState = (
    subAccount: ExtendedNestedSubAccount,
    id: string,
    open: boolean,
  ): ExtendedNestedSubAccount => {
    return {
      ...subAccount,
      isBlurred: open ? subAccount.id !== id : false,
      isOpen: subAccount.id === id ? open : false,
      children: subAccount.children?.map((child) => changeMenuState(child, id, open)),
    };
  };

  const handleMenuSateChange = (id: string, open: boolean) => {
    if (extendedSubAccount) {
      const updatedSubAccount = changeMenuState(extendedSubAccount, id, open);
      setExtendedSubAccount(updatedSubAccount);

      return updatedSubAccount;
    }

    return extendedSubAccount;
  };

  const closeAllMenus = (subAccount: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
    return {
      ...subAccount,
      isBlurred: false,
      isOpen: false,
      children: subAccount.children?.map((child) => closeAllMenus(child)),
    };
  };

  const handleLoadNext = (parentID: string) => {
    if (extendedSubAccount) {
      setExtendedSubAccount(changePage(extendedSubAccount, parentID, 'next'));
    }
  };

  const handleLoadPrev = (parentID: string) => {
    if (extendedSubAccount) {
      setExtendedSubAccount(changePage(extendedSubAccount, parentID, 'prev'));
    }
  };

  async function handleOpenCreateSubAccount(node: ExtendedNestedSubAccount) {
    const resp = await dialog.open<boolean>(
      ({ onCancel, onConfirm }) => (
        <CreateSubAccount
          node={node}
          onClose={onCancel}
          onSuccess={() => {
            onConfirm(true);
          }}
        />
      ),
      { bare: true },
    );

    if (resp) {
      refresh();

      alertDialog.success(
        t('SuccessMessage.title', { defaultValue: 'The Sub-Account is now being processed.' }),
        t('SuccessMessage.message', {
          defaultValue:
            'The sub-account is being reviewed and if required, the sub-account will be sent an onboarding application (doesn’t apply for certain types of sub-accounts). You can track the status of any sub-accounts through the sub-account listing.',
        }),
      );
    }
  }

  async function handleOpenAccountTransfer(node: ExtendedNestedSubAccount) {
    await dialog.open<AccountTransferToResponse>(({ onCancel }) => <AccountTransfer node={node} onClose={onCancel} />, {
      bare: true,
    });
  }

  const handleBalanceTypeChange = (nodeId: string, balanceType: SubAccountBalanceView) => {
    if (!extendedSubAccount) return;

    const updateNodeBalanceType = (node: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
      if (node.id === nodeId) {
        return { ...node, selectedBalanceType: balanceType };
      }

      return {
        ...node,
        children: node.children?.map(updateNodeBalanceType),
      };
    };

    setExtendedSubAccount(updateNodeBalanceType(extendedSubAccount));
  };

  const [isInitialState, setIsInitialState] = useState(true);

  const subListItems = useMemo(() => {
    const subAccounts = extendedSubAccount?.children || [];

    if (isInitialState) {
      return subAccounts.map((subAccount) => ({
        ...subAccount,
        isExpanded: false,
      }));
    }

    return subAccounts;
  }, [extendedSubAccount?.children, isInitialState]);

  const maxOpenDepth = useMemo(() => {
    const findMaxOpenDepth = (subAccount: ExtendedNestedSubAccount, currentDepth: number): number => {
      if (!subAccount.isExpanded) {
        return currentDepth;
      }

      return Math.max(
        currentDepth,
        ...(subAccount.children?.map((child) => findMaxOpenDepth(child, currentDepth + 1)) || []),
      );
    };

    return extendedSubAccount ? findMaxOpenDepth(extendedSubAccount, 0) : 0;
  }, [extendedSubAccount]);

  const SubAccountItem = ({
    subAccount,
    onSelect,
    onToggleExpand,
    lastItem = false,
  }: {
    subAccount: ExtendedNestedSubAccount;
    onSelect: (account: ExtendedNestedSubAccount) => void;
    onToggleExpand: (account: ExtendedNestedSubAccount) => void;
    lastItem?: boolean;
  }) => {
    const hasChildren = Array.isArray(subAccount.children) && subAccount.children.length > 0;

    return (
      <>
        <li
          className={cn('subAccountsListItem', subAccount.isExpanded && 'active', lastItem && 'lastItem')}
          onClick={() => {
            if (hasChildren) {
              onToggleExpand(subAccount);
            } else {
              onSelect(subAccount);
            }
          }}
        >
          <span className={cn('subAccountName')}>
            {maxOpenDepth == subAccount.level && subAccount.level !== 1 && (
              <MaterialSymbol icon="fiber_manual_record" fillAmount={1} size="14px" />
            )}

            {((subAccount.level !== 1 && maxOpenDepth != subAccount.level) ||
              (subAccount.isExpanded && subAccount.level === 1)) && (
              <MaterialSymbol icon="remove" color="#2D2D2D80" size="16px" />
            )}

            {subAccount.AccountName}
          </span>

          {hasChildren && (
            <MaterialSymbol icon={subAccount.isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size="24px" />
          )}
        </li>

        {hasChildren && subAccount.isExpanded && (
          <>
            {subAccount.children?.map((child, index, array) => (
              <SubAccountItem
                key={child.Key}
                subAccount={child}
                onSelect={onSelect}
                onToggleExpand={onToggleExpand}
                lastItem={index === array.length - 1}
              />
            ))}
          </>
        )}
      </>
    );
  };

  return (
    <div className={cn('container', isExpand && 'expanded')}>
      {!isExpand && (
        <div className={cn('subAccountsListContainer')}>
          <ul data-testid="orgChartContainer-subAccountsList" className={cn('subAccountsList', 'scrollbar')}>
            {subListItems.map((subAccount) => (
              <SubAccountItem
                key={subAccount.Key}
                subAccount={subAccount}
                onSelect={(subAccount) => {
                  extendedSubAccount &&
                    setExtendedSubAccount(closeAllMenus(revealNode(extendedSubAccount, subAccount.id, showUpto)));

                  setMenuOpen(false);

                  setTimeout(() => {
                    if (chartRef.current) {
                      chartRef.current.setCentered(subAccount.id).render();
                    }
                  }, 700);
                }}
                onToggleExpand={(subAccount) => {
                  extendedSubAccount &&
                    setExtendedSubAccount(
                      revealNode(
                        closeAllMenus(
                          toggleNode(extendedSubAccount, {
                            id: subAccount.id,
                            level: subAccount.level,
                            ancestorIDs: subAccount.parentsIds,
                            isExpanded: isInitialState ? true : undefined,
                          }),
                        ),
                        subAccount.id,
                        showUpto,
                      ),
                    );

                  setIsInitialState(false);

                  setMenuOpen(false);

                  setTimeout(() => {
                    if (chartRef.current) {
                      chartRef.current.setCentered(subAccount.id).render();
                    }
                  }, 700);
                }}
              />
            ))}
          </ul>
        </div>
      )}

      <div className={cn('orgChartAreaContainer')}>
        <Box flx="row" jc="sb">
          <Box gapSize="16px" ai="center" className={cn('orgChartHeader')}>
            <Box gapSize="4px" ai="center">
              <div>{t('DisplayLevels.prefix')}</div>

              <PulloverSelect
                name="displayLevel"
                value={String(displayLevel)}
                onChange={(value) => setDisplayLevel(Number(value))}
              >
                <PulloverSelect.Option value="1">{t('DisplayLevels.suffix', { level: '1' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="2">{t('DisplayLevels.suffixPlur', { level: '2' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="3">{t('DisplayLevels.suffixPlur', { level: '3' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="4">{t('DisplayLevels.suffixPlur', { level: '4' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="5">{t('DisplayLevels.suffixPlur', { level: '5' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="6">{t('DisplayLevels.suffixPlur', { level: '6' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="7">{t('DisplayLevels.suffixPlur', { level: '7' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="8">{t('DisplayLevels.suffixPlur', { level: '8' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="9">{t('DisplayLevels.suffixPlur', { level: '9' })}</PulloverSelect.Option>

                <PulloverSelect.Option value="10">
                  {t('DisplayLevels.suffixPlur', { level: '10' })}
                </PulloverSelect.Option>
              </PulloverSelect>

              <div></div>
            </Box>

            <Box gapSize="4px" ai="center">
              <div>{t('ShowUpToSubAccounts.prefix')}</div>

              <PulloverSelect name="showUpTo" value={String(showUpto)} onChange={(value) => setShowUpto(Number(value))}>
                <PulloverSelect.Option value="5">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '5' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="10">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '10' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="15">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '15' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="20">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '20' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="25">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '25' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="30">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '30' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="40">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '40' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="50">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '50' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="75">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '75' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="100">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '100' })}
                </PulloverSelect.Option>
              </PulloverSelect>
            </Box>

            <Box gapSize="4px" ai="center">
              <div>{t('BalancesLabel.label', { defaultValue: 'Balances' })}</div>

              <PulloverSelect
                name="displayBalance"
                value={displayBalance}
                onChange={(value) => setDisplayBalance(value)}
              >
                <PulloverSelect.Option value="do-not-show-balances">
                  {t('BalancesLabel.doNotShowBalances', { defaultValue: "Don't Show Balances" })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="roll-up-balance">
                  {t('BalancesLabel.rollUpBalance', { defaultValue: 'Available Roll-Up Balance' })}
                </PulloverSelect.Option>

                <PulloverSelect.Option value="available-balance">
                  {t('BalancesLabel.availableBalance', { defaultValue: 'Available Balance' })}
                </PulloverSelect.Option>
              </PulloverSelect>
            </Box>
          </Box>

          <UtilButton
            onClick={() => {
              resetList();
            }}
            iconLeft="replay"
          >
            {t('resetButton')}
          </UtilButton>
        </Box>

        <div data-testid="orgChartContainer" className={cn('orgChartContainer')}>
          {useMemo(() => {
            if (fetchingBalances) {
              return <LoadingIndicator isVisible size="lg" hasNoMessage hasNoTitle />;
            }

            return (
              <OrgChart
                tree={extendedSubAccount}
                pageSize={showUpto}
                displayLevel={displayLevel}
                keepChildrenWhole={(node) => node.isRoot}
                getExtraChildren={getRootServerPageNodes}
                nodeHeight={(node) => {
                  if (node.nodeType === 'prevTree') {
                    return 32;
                  }

                  return displayBalance === 'do-not-show-balances' ? 98 : 126;
                }}
                nodeWidth={(node) => {
                  if (node.nodeType === 'prevTree') {
                    return 73;
                  }

                  return 180;
                }}
                chartRef={chartRef}
                siblingsMargin={33}
                childrenMargin={48}
                neighborMargin={33}
                onNodeClick={() => {}}
                renderComponent={(node) => {
                  if (node.nodeType === 'accountNode') {
                    return (
                      <AccountNode
                        balances={balances}
                        displayBalance={displayBalance}
                        accountName={node.AccountName}
                        isBlurred={node.isBlurred}
                      />
                    );
                  } else if (node.nodeType === 'loadNext') {
                    // Load-more nodes carry their first sibling's breadcrumb, which ends with the parent's name.
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button
                          className={cn('loadMore')}
                          type="button"
                          aria-label={t('loadNextButtonLabel', {
                            nodes: Math.min(node.numChildren, showUpto),
                            parentName: node.breadcrumb.at(-1),
                            defaultValue: 'Show Next {{nodes}} under {{parentName}}',
                          })}
                          onClick={() => handleLoadNext(node.parentId)}
                        >
                          <MaterialSymbol icon="cached" />

                          {t('loadNextButton', {
                            nodes: Math.min(node.numChildren, showUpto),
                          })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'loadPrevious') {
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button
                          className={cn('loadMore')}
                          type="button"
                          aria-label={t('loadPrevButtonLabel', {
                            nodes: Math.min(node.numChildren, showUpto),
                            parentName: node.breadcrumb.at(-1),
                            defaultValue: 'Show Prev {{nodes}} under {{parentName}}',
                          })}
                          onClick={() => handleLoadPrev(node.parentId)}
                        >
                          <MaterialSymbol icon="cached" />

                          {t('loadPrevButton', {
                            nodes: Math.min(node.numChildren, showUpto),
                          })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'loadNextServerPage') {
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button className={cn('loadMore')} type="button" onClick={() => rootNextPage?.()}>
                          <MaterialSymbol icon="cached" />
                          {t('loadNextServerPageButton', { nodes: rootPageSize, defaultValue: 'Next {{nodes}}' })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'loadPreviousServerPage') {
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button className={cn('loadMore')} type="button" onClick={() => rootPrevPage?.()}>
                          <MaterialSymbol icon="cached" />
                          {t('loadPrevServerPageButton', { nodes: rootPageSize, defaultValue: 'Previous {{nodes}}' })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'prevTree') {
                    return (
                      <button
                        className={cn('prevTree')}
                        type="button"
                        aria-label={t('prevTreeButton', { name: node.AccountName, defaultValue: 'Show {{name}}' })}
                        onClick={() => handleExpand(node.id, node.level, node.parentsIds)}
                      >
                        <MaterialSymbol icon="more_horiz" />
                      </button>
                    );
                  } else {
                    return (
                      <OrgItem
                        node={node}
                        canCreateAccountTransfer={canCreateAccountTransfer}
                        accountBlocked={accountBlocked}
                        canManageBlocking={canManageBlocking}
                        onLocationChange={(location, state) => {
                          history.push(location, state);
                        }}
                        onToggleExpand={(node) => handleExpand(node.id, node.level, node.parentsIds)}
                        onChangeMenuState={(open, node) => {
                          handleMenuSateChange(node.id, open);
                          setMenuOpen(open);
                        }}
                        openCreateSubAccount={handleOpenCreateSubAccount}
                        openAccountTransfer={handleOpenAccountTransfer}
                        displayBalance={displayBalance}
                        onBalanceTypeChange={handleBalanceTypeChange}
                        onBlockAccount={onBlockAccount}
                        onUnblockAccount={onUnblockAccount}
                      />
                    );
                  }
                }}
              />
            );
          }, [
            extendedSubAccount,
            showUpto,
            displayLevel,
            hasRootNextServerPage,
            hasRootPrevServerPage,
            balances,
            accountBlocked,
          ])}

          {menuOpen && <div className={cn('overlay')} />}
        </div>
      </div>

      <div className={cn('expandButtonContainer')}>
        {isExpand && (
          <CircleButton
            colorScheme="action"
            onClick={() => {
              setIsExpand(false);
            }}
            icon="zoom_in_map"
          />
        )}

        {!isExpand && (
          <CircleButton
            colorScheme="action"
            onClick={() => {
              setIsExpand(true);
            }}
            icon="zoom_out_map"
          />
        )}
      </div>
    </div>
  );
}
